import { useCallback, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { CampaignDraftAPI } from "src/services/CampaignDraftAPI";
import type {
  CampaignDraft,
  CampaignDraftContent,
  CampaignDraftData,
} from "src/services/CampaignDraftTypes";
import { getThrownApiDetail } from "src/utils/showApiDetailToast";

interface DraftVersion {
  id: number;
  updatedAt: string;
}

const toVersion = (draft: Pick<CampaignDraft, "draft_id" | "updated_at">): DraftVersion => ({
  id: draft.draft_id,
  updatedAt: draft.updated_at,
});

/** The backend draft behind one wizard: created on the first save, updated on every later one. */
export const useCampaignDraft = (initialDraft: CampaignDraft | null = null) => {
  const { t } = useTranslation();
  const [version, setVersion] = useState<DraftVersion | null>(() =>
    initialDraft ? toVersion(initialDraft) : null
  );
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const save = useCallback(
    async (data: CampaignDraftData, content?: CampaignDraftContent | null): Promise<boolean> => {
      if (isSavingRef.current) {
        return false;
      }
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        const draft = version
          ? await CampaignDraftAPI.save(version.id, {
              data,
              content,
              expectedUpdatedAt: version.updatedAt,
            })
          : await CampaignDraftAPI.create("email", data, content);
        setVersion(toVersion(draft));
        setSavedSnapshot(JSON.stringify(data));
        message.success(t("campaignFlow.common.saveDraftSuccess"));
        return true;
      } catch (error) {
        message.error(getThrownApiDetail(error) || t("campaignFlow.common.saveDraftFailed"));
        return false;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [t, version]
  );

  /** Keeps the saved version in step with a server write made outside `save`, such as generating a template. */
  const syncUpdatedAt = useCallback((updatedAt?: string | null) => {
    if (updatedAt) {
      setVersion(current => (current ? { ...current, updatedAt } : current));
    }
  }, []);

  /** Called once the campaign is sent or scheduled: the backend closes the draft. */
  const closeDraft = useCallback(() => {
    setVersion(null);
    setSavedSnapshot(null);
  }, []);

  return {
    draftId: version?.id ?? null,
    isSaving,
    savedSnapshot,
    save,
    syncUpdatedAt,
    closeDraft,
  };
};

export type CampaignDraftState = ReturnType<typeof useCampaignDraft>;
